import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
const Frame203: React.FC = () => {
  const navigate = useNavigate();
  const [chatInput, setChatInput] = useState('');
  const [beneficiaryResolved, setBeneficiaryResolved] = useState(false);

  // Get assets from session - find one without beneficiary for demo
  const sampleAsset = {
    name: "Bank of America Savings",
    account: "01-23842-281",
    value: "$24,283.76",
    lastUpdated: "As of 3/2/2025 5:04 PM EST"
  };

  const handleClose = () => {
    navigate('/frame-196');
  };

  const handleNext = () => {
    if (beneficiaryResolved) {
      // Will route to Frame204 when implemented
      navigate('/frame-196'); // Temporary route back to summary
    }
  };

  const handleChatSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (chatInput.trim()) {
      setBeneficiaryResolved(true);
      setChatInput('');
    }
  };

  return (
    <div className="min-h-screen w-full bg-stone-50 flex flex-col">
      {/* Main Content Container */}
      <div className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
        
        {/* Content Grid - Responsive Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-full">
          
          {/* Left Content */}
          <div className="lg:col-span-2">
            {/* Header Section */}
            <div className="mb-8">
              <h1 className="text-cyan-900 text-2xl sm:text-3xl font-semibold font-['Test_Domaine_Display'] leading-loose">
                Final review
              </h1>
            </div>
            
            {/* Main Content */}
            <div className="max-w-4xl text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
              <span>
                This walkthrough will help you prepare all your estate documentation for creating a legally conforming will and/or trust.
              </span>
              <br/><br/>
              <span className="font-semibold">
                It looks like something is missing. 
              </span>
              <span>
                Please review:
              </span>
            </div>
          </div>
          
          {/* Virtual Assistant Panel - Responsive */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg h-full flex flex-col">
              
              {/* Assistant Header */}
              <div className="bg-stone-100 p-4 rounded-t-lg flex justify-between items-center">
                <h3 className="text-stone-800 text-base font-medium font-['Work_Sans']">
                  End of an Era virtual assistant
                </h3>
                <button 
                  onClick={handleClose}
                  className="w-3.5 h-3.5 bg-stone-500 hover:bg-stone-600 transition-colors rounded"
                />
              </div>
              
              {/* Assistant Content */}
              <div className="flex-1 p-4 flex flex-col gap-4">
                
                {/* Assistant Message */}
                <div className="text-black text-sm font-normal font-['Work_Sans'] leading-relaxed">
                  One of your assets is missing a beneficiary:
                </div>
                
                {/* Asset Warning Card */}
                <div className="bg-orange-100 border border-orange-300 rounded p-4 relative">
                  <div className="absolute top-2 right-2 w-4 h-3.5 bg-black" />
                  <div className="space-y-2">
                    <div className="text-black text-base font-medium font-['Work_Sans']">
                      {sampleAsset.name}
                    </div>
                    <div className="text-black/50 text-xs font-medium font-['Work_Sans']">
                      Account # {sampleAsset.account}
                    </div>
                    <div className="text-black text-base font-semibold font-['Inter']">
                      {sampleAsset.value}
                    </div>
                    <div className="text-black/50 text-xs font-medium font-['Work_Sans']">
                      {sampleAsset.lastUpdated}
                    </div>
                  </div>
                </div>
                
                {/* Follow-up Question */}
                <div className="text-black text-sm font-normal font-['Work_Sans'] leading-relaxed">
                  How would you like this account to be passed on?
                </div>
                
                {/* Spacer */}
                <div className="flex-1" />
                
                {/* Chat Input */}
                <form onSubmit={handleChatSubmit} className="mt-auto">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Type your response..."
                    className="w-full h-9 bg-white rounded border border-stone-300 px-3 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                  />
                </form>
              </div>
            </div>
          </div>
        </div>
        
        {/* Chat button - Fixed position */}
        <div className="fixed bottom-6 right-6 w-12 h-12 bg-slate-100 rounded-3xl shadow-lg flex items-center justify-center">
          <div className="w-5 h-5 bg-cyan-900" />
        </div>
      </div>

      {/* Footer Navigation - Responsive */}
      <footer className="w-full bg-stone-50 border-t border-stone-200">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-cyan-900 rounded-md shadow-lg mx-4 mb-4 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              
              {/* Footer Content */}
              <div className="flex-1">
                <h3 className="text-stone-50 text-xl font-medium font-['Work_Sans'] leading-relaxed mb-2">
                  Finalization
                </h3>
                <p className="text-stone-50/50 text-xs font-normal font-['Work_Sans'] leading-none">
                  Approximately 10 minutes
                </p>
                
                {/* Progress bar */}
                <div className="mt-4 w-full max-w-2xl">
                  <div className="w-full h-[5px] bg-stone-400 rounded-md relative">
                    <div className="w-32 h-[5px] bg-green-900 rounded-md absolute left-0 top-0" />
                  </div>
                </div>
              </div>
              
              {/* Next Button */}
              <button 
                onClick={handleNext}
                disabled={!beneficiaryResolved}
                className={`px-6 py-3 rounded inline-flex justify-center items-center gap-2.5 transition-colors whitespace-nowrap ${
                  beneficiaryResolved 
                    ? 'bg-cyan-700 hover:bg-cyan-800 cursor-pointer opacity-100' 
                    : 'bg-cyan-700 opacity-50 cursor-not-allowed'
                }`}
              >
                <span className="text-center text-stone-50 text-base font-medium font-['Work_Sans'] leading-none">
                  Next →
                </span>
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Frame203; 